import React, { useState } from "react";
import { Container } from "@mui/material";
import "./index.scss";
import SuccessModal from "../successModal";
import ThankYouDialog from "../thankyouModal";
import FormField from "./components/FormField";
import ProductSelect from "./components/ProductSelect";
import { useFormState } from "./hooks/useFormState";
import { useFormSubmission } from "./hooks/useFormSubmission";

const Form = () => {
  // Form state management
  const {
    form,
    errors,
    fetchingPin,
    handleFieldChange,
    handleFieldBlur,
    validateForm,
    resetForm,
  } = useFormState();

  // Form submission management
  const { submitting, submitMsg, submitForm, submitWarrantyUpdate } =
    useFormSubmission();

  // Modal state
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openThankYouModal, setOpenThankYouModal] = useState(false);
  const [warrantyChecked, setWarrantyChecked] = useState(false);

  // Event handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    handleFieldChange(name, value);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    handleFieldBlur(name, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const result = await submitForm(form);

    if (result.success) {
      resetForm();
      setOpenSuccessModal(true);
    }
  };

  const handleWarrantyNext = async () => {
    setOpenSuccessModal(false);
    if (warrantyChecked) {
      await submitWarrantyUpdate(warrantyChecked);
    }
    setOpenThankYouModal(true);
  };

  // Calculate date restrictions for purchase date
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const maxDate = today.toISOString().split("T")[0];
  const minDate = thirtyDaysAgo.toISOString().split("T")[0];

  return (
    <>
      <Container maxWidth={false} sx={{ p: 0 }} className="main-form-container">
        <form className="form-container" onSubmit={handleSubmit} noValidate>
          {/* Name and Email */}
          <div className="form-row">
            <div className="form-half">
              <FormField
                id="name"
                name="name"
                label="Name"
                value={form.name}
                error={!!errors.name}
                helperText={errors.name || ""}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>

            <div className="form-half">
              <FormField
                id="email"
                name="email"
                label="Email ID"
                value={form.email}
                error={!!errors.email}
                helperText={errors.email || ""}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
          </div>

          {/* Mobile and Product */}
          <div className="form-row">
            <div className="form-half">
              <FormField
                id="mobile"
                name="mobile"
                label="Mobile Number"
                value={form.mobile}
                error={!!errors.mobile}
                helperText={errors.mobile || ""}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>

            <div className="form-half">
              <ProductSelect
                value={form.product}
                error={!!errors.product}
                helperText={errors.product || ""}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
          </div>

          {/* Serial Number and Invoice Number */}
          <div className="form-row">
            <div className="form-half">
              <FormField
                id="serial"
                name="serial"
                label="Product Serial Number"
                value={form.serial}
                error={!!errors.serial}
                helperText={errors.serial || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                inputProps={{
                  maxLength: 25,
                }}
              />
            </div>

            <div className="form-half">
              <FormField
                id="invoiceNumber"
                name="invoiceNumber"
                label="Invoice Number"
                value={form.invoiceNumber}
                error={!!errors.invoiceNumber}
                helperText={errors.invoiceNumber || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                inputProps={{
                  maxLength: 25,
                }}
              />
            </div>
          </div>

          {/* Purchase Date and Pincode */}
          <div className="form-row">
            <div className="form-half">
              <FormField
                id="purchaseDate"
                name="purchaseDate"
                label="Date of Purchase"
                type="date"
                value={form.purchaseDate}
                error={!!errors.purchaseDate}
                helperText={errors.purchaseDate || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                slotProps={{
                  inputLabel: { shrink: true },
                }}
                inputProps={{
                  min: minDate,
                  max: maxDate,
                  onKeyDown: (e) => e.preventDefault(),
                }}
              />
            </div>

            <div className="form-half">
              <FormField
                id="pincode"
                name="pincode"
                label="City Pin code"
                value={form.pincode}
                error={!!errors.pincode}
                helperText={errors.pincode || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                inputProps={{
                  maxLength: 6,
                  pattern: "[0-9]{6}",
                }}
              />
            </div>
          </div>

          {/* City and State (Read-only) */}
          <div className="form-row">
            <div className="form-half">
              <FormField
                id="city"
                name="city"
                label="City"
                value={fetchingPin ? "Fetching..." : form.city}
                readOnly
              />
            </div>

            <div className="form-half">
              <FormField
                id="state"
                name="state"
                label="State"
                value={fetchingPin ? "Fetching..." : form.state}
                readOnly
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="form-submit-button"
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>

          {/* Submit Message */}
          {submitMsg && <div className="form-submit-message">{submitMsg}</div>}
        </form>
      </Container>

      {/* Success Modal */}
      <SuccessModal
        open={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
        onNext={handleWarrantyNext}
        warrantyChecked={warrantyChecked}
        onWarrantyChange={(e) => setWarrantyChecked(e.target.checked)}
      />

      {/* Thank You Modal */}
      {openThankYouModal && (
        <ThankYouDialog
          open={openThankYouModal}
          onClose={() => setOpenThankYouModal(false)}
        />
      )}
    </>
  );
};

export default Form;
